import { assign, fromPromise, setup } from 'xstate';
import type { JobResponse } from '~/types/hlx/jobResponse';
import {
  sendInitialInquiry,
  type InitialInquiryInput,
  type InitialInquiryTarget,
} from './initialInquiry';
import { ExistingInquiryError } from './initialInquiryReceipt';
import {
  contentScreeningFeedback,
  isUncertainListingActionError,
  shouldKeepMessageEditorOpen,
} from './listing-search/errors';

export type InquirySource = { target: InitialInquiryTarget; title: string };
const sourceKey = (source: InquirySource | null) => {
  const target = source?.target;
  if (!target) return '';
  return target.listingType === 'JOB'
    ? `JOB:${target.jobListingId}`
    : target.listingType === 'MSAAS'
      ? `MSAAS:${target.msaasListingId}`
      : `SERVICE:${target.providerServiceListingId}`;
};

export const initialInquiryMachine = setup({
  types: {
    context: {} as {
      source: InquirySource | null;
      draft: string;
      open: boolean;
      uncertain: boolean;
      error: string | null;
      attempt: InitialInquiryInput | null;
      result: JobResponse | null;
      existingResponse: JobResponse | null;
    },
    events: {} as
      | { type: 'OPEN'; source: InquirySource }
      | { type: 'CLOSE' }
      | { type: 'EDIT'; value: string }
      | { type: 'SEND' }
      | { type: 'RETRY' }
      | { type: 'DISCARD_RETRY' },
  },
  actors: {
    send: fromPromise(({ input, signal }: { input: InitialInquiryInput; signal: AbortSignal }) =>
      sendInitialInquiry(input, signal)
    ),
  },
}).createMachine({
  id: 'initialInquiry',
  initial: 'ready',
  context: {
    source: null,
    draft: '',
    open: false,
    uncertain: false,
    error: null,
    attempt: null,
    result: null,
    existingResponse: null,
  },
  states: {
    ready: {
      on: {
        OPEN: {
          actions: assign(({ context, event }) => {
            const sameSource = sourceKey(context.source) === sourceKey(event.source);
            if (context.uncertain) return { open: sameSource };
            const target = event.source.target;
            return {
              source: {
                ...event.source,
                target:
                  target.listingType === 'MSAAS'
                    ? { ...target, providerIds: [...(target.providerIds ?? [])] }
                    : { ...target },
              },
              open: true,
              draft: sameSource ? context.draft : '',
              error: null,
              result: null,
              existingResponse: sameSource ? context.existingResponse : null,
            };
          }),
        },
        CLOSE: { actions: assign({ open: false }) },
        EDIT: {
          guard: ({ context }) => !context.uncertain,
          actions: assign({ draft: ({ event }) => event.value, error: null }),
        },
        SEND: {
          guard: ({ context }) =>
            context.open &&
            !!context.source &&
            !!context.draft.trim() &&
            !context.uncertain &&
            !context.existingResponse,
          target: 'sending',
          actions: assign(({ context }) => ({
            attempt: context.source
              ? { ...context.source.target, messageBody: context.draft.trim() }
              : null,
            error: null,
            result: null,
            existingResponse: null,
          })),
        },
        RETRY: {
          guard: ({ context }) => context.open && context.uncertain && context.attempt !== null,
          target: 'sending',
          actions: assign({ error: null }),
        },
        DISCARD_RETRY: {
          guard: ({ context }) => context.uncertain,
          actions: assign({
            source: null,
            draft: '',
            open: false,
            uncertain: false,
            error: null,
            attempt: null,
            result: null,
          }),
        },
      },
    },
    sending: {
      invoke: {
        src: 'send',
        input: ({ context }) => {
          if (!context.attempt) throw new Error('Select a listing before messaging.');
          return context.attempt;
        },
        onDone: {
          target: 'ready',
          actions: assign({
            result: ({ event }) => event.output,
            draft: '',
            attempt: null,
            uncertain: false,
            open: false,
            error: null,
          }),
        },
        onError: {
          target: 'ready',
          actions: assign(({ context, event }) => {
            if (event.error instanceof ExistingInquiryError) {
              return {
                existingResponse: event.error.jobResponse,
                uncertain: false,
                attempt: null,
                error: event.error.message,
              };
            }
            const uncertain = context.uncertain || isUncertainListingActionError(event.error);
            return {
              uncertain,
              error: uncertain
                ? 'We could not confirm whether your inquiry was sent. Retry the original inquiry to recover the existing conversation; it will not create a duplicate initial message.'
                : shouldKeepMessageEditorOpen(event)
                  ? (contentScreeningFeedback(event.error) ??
                    'Message not sent. Remove names or contact information if present. If screening is unavailable, try again later. Your draft is preserved.')
                  : 'Unable to send this inquiry. Your draft is preserved; check that the listing is still available before trying again.',
            };
          }),
        },
      },
    },
  },
});
